import React, { useState } from 'react'
import { useTranslation } from 'language/translator'

import { KpiOptionObject } from 'types/GlobalKpiOption'
import { TWidgetSettings, WidgetClass, WidgetType } from 'types/GlobalWidget'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsItem from 'components_new/molecules/SettingsItem'

import { getBreakdownDisabledText, getBreakdownOptions } from './utils'
import {
  useBreakdownBy,
  useKpiOptionAttributeOptions
} from 'redux/hooks/AttributeOptions'
import { Option } from 'components_new/molecules/SelectSearch'
import { TPatchKpiOptionBody } from 'redux/api/KpiOptions'

interface BreakdownSectionProps {
  kpi: KpiOptionObject
  updateKpiOption: (body: TPatchKpiOptionBody) => void
  settings: TWidgetSettings
  widgetClass: WidgetClass
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.LINE_CHART,
  WidgetType.LIST
]

const BreakdownSection = (props: BreakdownSectionProps) => {
  const { kpi, settings, updateKpiOption, widgetClass } = props
  const { t } = useTranslation()

  const breakdownBy = useBreakdownBy(kpi)
  const breakdownOptions = useKpiOptionAttributeOptions(
    settings,
    kpi,
    'breakdown_attribute'
  )

  const [dialog, setDialog] = useState<{
    open: boolean
    data: {
      options: Option[]
      value: string
    }
  }>({
    open: false,
    data: {
      options: [],
      value: ''
    }
  })

  if (
    !LEGAL_TYPES.includes(settings.type.selected) ||
    widgetClass === WidgetClass.EXPORT_TABLE
  ) {
    return null
  }

  /*-- dialogs --*/
  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = () => {
    setDialog({
      open: true,
      data: {
        options: getBreakdownOptions(breakdownOptions, settings, t),
        value: kpi.breakdown_by.selected ?? ''
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    updateKpiOption({
      breakdown_by: dialog.data.value === 'none' ? null : dialog.data.value
    })
  }

  const selectedBreakdown = breakdownBy ? breakdownBy.name : null
  const disabledText = getBreakdownDisabledText(settings, t)

  return (
    <>
      <SettingsGroup title={t('widgets', 'breakdown_title')}>
        <SettingsItem
          disabled={Boolean(disabledText)}
          disabledText={disabledText}
          onClick={() => handleOpenDialog()}
          title={t('widgets', 'breakdown_by')}
          value={selectedBreakdown}
          variant="edit"
        />
      </SettingsGroup>

      {/*-- dialogs --*/}
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={t('widgets', 'breakdown_by')}
        type={'select'}
        value={dialog.data.value}
      />
    </>
  )
}

export default BreakdownSection
